import { CircleInfoIcon } from "@xola/icons";
import { Badge, Input, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { useFormikContext } from "formik";
import React, { ChangeEvent, useCallback } from "react";
import { Experience, ReviewDestination, ReviewPreference } from "types";
import { ContentLayout } from "@/components/Layout/ContentLayout";

interface CustomReviewSmsTemplateProps {
    experience?: Experience;
    sendSMS: boolean;
    setSendSMS: (checked: boolean) => void;
}

export const CustomReviewSmsTemplate = ({ experience, sendSMS, setSendSMS }: CustomReviewSmsTemplateProps) => {
    const { values, setFieldValue, errors } = useFormikContext<ReviewPreference>();

    const handleReviewLinkChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setFieldValue("reviewLink", e.target.value);
        },
        [setFieldValue],
    );

    const handleSmsToggle = (checked: boolean) => {
        setSendSMS(checked);
    };

    if (values.destination !== ReviewDestination.CUSTOM) return null;

    return (
        <ContentLayout.Section
            title="SMS Template"
            description="Customize the review link that will be sent in the text message"
        >
            <div className="space-y-4">
                <div className="flex items-center space-x-2">
                    <Switch isChecked={sendSMS} size="medium" onChange={handleSmsToggle} />

                    <h5>Configure SMS Template</h5>
                </div>
                {sendSMS && (
                    <>
                        <div>
                            <span className={clsx(errors?.reviewLink && "!text-danger", "text-sm font-bold")}>
                                Review URL {errors?.reviewLink ? ` ${errors.reviewLink}` : ""}
                            </span>
                            <Input
                                value={values.reviewLink ?? ""}
                                placeholder="Paste review link here"
                                isError={!!errors?.reviewLink}
                                onChange={handleReviewLinkChange}
                            />
                        </div>
                        <div>
                            <span className="mt-4 text-sm font-bold">Message Preview</span>
                            <div>
                                <span>How was</span>
                                <Tooltip content="Experience that the customer attended" placement="bottom">
                                    <Badge color="secondary" className="flex items-center space-x-2">
                                        <span>{experience?.name}</span>
                                    </Badge>
                                </Tooltip>
                                <span>? Leave a review: </span>
                                <span className="mr-1 break-all underline">{values.reviewLink}</span>
                                <Tooltip
                                    content="The URL will be shortened in the SMS sent to your customer"
                                    placement="bottom"
                                >
                                    <CircleInfoIcon />
                                </Tooltip>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </ContentLayout.Section>
    );
};
